/**
 * Licensing Page
 *
 * License activation and management interface for Multilingual Press Zone.
 * Handles license validation, usage tracking, and plan upgrades.
 *
 * @package MultilingualPressZone
 * @since 1.0.0
 */

import { el, __, formatNumber, clear } from '../utils/dom.js';
import api from '../utils/api.js';
import logger from '../utils/logger.js';
import Badge from '../components/Badge.js';
import ProgressBar from '../components/ProgressBar.js';
import { Button } from '../components/Button.js';
import Toast from '../components/Toast.js';
import { Spinner } from '../components/Spinner.js';
import { PageHeader } from '../components/PageHeader.js';
import { Card } from '../components/Card.js';
import EmptyState from '../components/EmptyState.js';
import Modal from '../components/Modal.js';

/**
 * License Plans Configuration (fallback if API fetch fails)
 */
const LICENSE_PLANS = {
    personal: {
        name: 'Personal',
        slug: 'starter',
        price: 50,
        interval: 'year',
        sites: 3,
        languages: 1,
        features: [
            'Up to 3 sites',
            '1 translation language',
            'Email support',
            'Automatic updates',
            'Machine translation via Translate Press Zone'
        ]
    },
    professional: {
        name: 'Professional',
        slug: 'professional',
        price: 120,
        interval: 'year',
        sites: 25,
        languages: 5,
        features: [
            'Up to 25 sites',
            'Up to 5 translation languages',
            'Priority email support',
            'Automatic updates',
            'Machine translation via Translate Press Zone',
            'Advanced glossary management'
        ],
        popular: true
    },
    agency: {
        name: 'Agency',
        slug: 'enterprise',
        price: 350,
        interval: 'year',
        sites: -1, // Unlimited
        languages: -1, // Unlimited
        features: [
            'Unlimited sites',
            'Unlimited translation languages',
            'Priority support + Slack',
            'Automatic updates',
            'Machine translation via Translate Press Zone',
            'Advanced glossary management',
            'White-label options',
            'Custom translation models'
        ]
    }
};

/**
 * LicensingPage Class
 *
 * Main class for the licensing page interface
 */
export default class LicensingPage {
    /**
     * Constructor
     *
     * @param {HTMLElement} container - Container element to render into
     */
    constructor(container) {
        this.container = container;
        this.license = null;
        this.usage = null;
        this.usageChart = null;
        this.progressBar = null;
        this._upgradePaypalPollInterval = null;

        logger.info('Licensing page initialized');
    }

    /**
     * Render the licensing page
     */
    async render() {
        clear(this.container);
        this.container.className = 'mpz-licensing-page';

        const header = PageHeader({
            title: __('Licensing', 'multilingual-press-zone'),
            description: __('Manage your Multilingual Press Zone license and view usage statistics.', 'multilingual-press-zone')
        });

        this.container.appendChild(header);

        // Loading state while we fetch license/usage.
        this.container.appendChild(
            el('div', { class: 'presszone-multilingual-loading' },
                Spinner({ size: 'md', label: __('Loading licensing data...', 'multilingual-press-zone') }),
                el('p', {}, __('Loading licensing data...', 'multilingual-press-zone'))
            )
        );

        await this.loadLicense();

        // Re-render fully after load.
        clear(this.container);
        this.container.className = 'mpz-licensing-page';
        this.container.appendChild(header);

        // Render based on license status
        if (this.license && this.license.status === 'active') {
            this.renderActiveLicense();
        } else {
            this.renderInactiveLicense();
        }
    }

    /**
     * Render active license section
     */
    renderActiveLicense() {
        const content = el('div', { class: 'mpz-licensing-content' });

        // License status card
        const statusCard = this.renderLicenseStatusCard();
        content.appendChild(statusCard);

        // Usage statistics card
        const usageCard = this.renderUsageCard();
        content.appendChild(usageCard);

        // Upgrade options card
        const upgradeCard = this.renderUpgradeCard();
        content.appendChild(upgradeCard);

        this.container.appendChild(content);
    }

    /**
     * Render inactive license section
     */
    renderInactiveLicense() {
        const content = el('div', { class: 'mpz-licensing-content mpz-licensing-content--inactive' });

        // Activation card
        const activationCard = this.renderActivationCard();
        content.appendChild(activationCard);

        // Plans comparison card
        const plansCard = this.renderPlansCard();
        content.appendChild(plansCard);

        this.container.appendChild(content);
    }

    /**
     * Render license status card
     *
     * @return {HTMLElement} Status card element
     */
    renderLicenseStatusCard() {
        const plan = LICENSE_PLANS[this.license.plan] || LICENSE_PLANS.personal;
        const statusBadge = new Badge({
            text: this.getLicenseStatusText(this.license.status),
            variant: this.getLicenseStatusVariant(this.license.status),
            size: 'medium'
        });

        const cardBody = el('div', { class: 'mpz-license-status' },
            // Plan name and status
            el('div', { class: 'mpz-license-status__header' },
                el('div', {},
                    el('h3', { class: 'mpz-license-status__plan' }, plan.name + ' ' + __('Plan', 'multilingual-press-zone')),
                    el('p', { class: 'mpz-license-status__price' },
                        '$' + plan.price + ' / ' + __('year', 'multilingual-press-zone')
                    )
                ),
                statusBadge.getElement()
            ),

            // License key
            el('div', { class: 'mpz-license-status__key' },
                el('label', {}, __('License Key', 'multilingual-press-zone')),
                el('code', {}, this.maskLicenseKey(this.license.key))
            ),

            // License details grid
            el('div', { class: 'mpz-license-status__details' },
                el('div', { class: 'mpz-license-status__detail' },
                    el('span', { class: 'mpz-license-status__detail-label' }, __('Sites Used', 'multilingual-press-zone')),
                    el('span', { class: 'mpz-license-status__detail-value' },
                        this.license.sites_used + ' / ' + (this.license.sites_allowed === -1 ? __('Unlimited', 'multilingual-press-zone') : (this.license.sites_allowed || plan.sites))
                    )
                ),
                el('div', { class: 'mpz-license-status__detail' },
                    el('span', { class: 'mpz-license-status__detail-label' }, __('Translation Languages', 'multilingual-press-zone')),
                    el('span', { class: 'mpz-license-status__detail-value' },
                        this.license.languages_allowed === -1 ? __('Unlimited', 'multilingual-press-zone') : (this.license.languages_allowed || plan.languages)
                    )
                ),
                el('div', { class: 'mpz-license-status__detail' },
                    el('span', { class: 'mpz-license-status__detail-label' }, __('Expires', 'multilingual-press-zone')),
                    el('span', { class: 'mpz-license-status__detail-value' }, this.formatDate(this.license.expires_at))
                )
            ),

            // Actions
            el('div', { class: 'mpz-license-status__actions' },
                Button({
                    label: __('Deactivate License', 'multilingual-press-zone'),
                    variant: 'ghost',
                    size: 'sm',
                    onClick: () => this.handleDeactivate()
                }),
                Button({
                    label: __('Renew License', 'multilingual-press-zone'),
                    variant: 'primary',
                    size: 'sm',
                    onClick: () => window.open('https://api.press.zone/account/licenses', '_blank')
                })
            )
        );

        return Card('🔑', __('License Status', 'multilingual-press-zone'), '', [cardBody], { staggerIndex: 0 });
    }

    /**
     * Render usage statistics card
     *
     * @return {HTMLElement} Usage card element
     */
    renderUsageCard() {
        const languagesUsed = this.usage.languages_used || 0;
        const languagesAllowed = this.usage.languages_allowed || 0;
        const isUnlimited = languagesAllowed === -1;

        // Create progress bar container
        const progressContainer = el('div', { class: 'mpz-usage-progress' });

        // Usage stats
        const usageStats = el('div', { class: 'mpz-usage-stats' },
            el('div', { class: 'mpz-usage-stat' },
                el('span', { class: 'mpz-usage-stat__label' }, __('Translation Languages', 'multilingual-press-zone')),
                el('span', { class: 'mpz-usage-stat__value' },
                    isUnlimited
                        ? languagesUsed + ' (' + __('Unlimited', 'multilingual-press-zone') + ')'
                        : languagesUsed + ' / ' + languagesAllowed
                )
            ),
            el('div', { class: 'mpz-usage-stat' },
                el('span', { class: 'mpz-usage-stat__label' }, __('Remaining', 'multilingual-press-zone')),
                el('span', { class: 'mpz-usage-stat__value' },
                    isUnlimited
                        ? __('Unlimited', 'multilingual-press-zone')
                        : String(Math.max(0, languagesAllowed - languagesUsed))
                )
            )
        );

        const cardBody = el('div', {},
            progressContainer,
            usageStats
        );

        // Initialize progress bar after DOM insertion (only if not unlimited)
        if (!isUnlimited && languagesAllowed > 0) {
            const usagePercent = (languagesUsed / languagesAllowed) * 100;
            setTimeout(() => {
                this.progressBar = new ProgressBar({
                    container: progressContainer,
                    value: languagesUsed,
                    max: languagesAllowed,
                    label: __('Language Usage', 'multilingual-press-zone'),
                    variant: usagePercent >= 100 ? 'error' : usagePercent >= 80 ? 'warning' : 'success',
                    showPercentage: true
                });
            }, 0);
        }

        return Card('📈', __('Language Usage', 'multilingual-press-zone'), '', [cardBody], { staggerIndex: 1 });
    }

    /**
     * Render upgrade options card
     *
     * @return {HTMLElement} Upgrade card element
     */
    renderUpgradeCard() {
        const currentPlan = this.license.plan;
        const upgradePlans = Object.entries(LICENSE_PLANS)
            .filter(([key]) => this.isPlanUpgrade(currentPlan, key))
            .map(([key, plan]) => this.renderPlanOption(key, plan));

        if (upgradePlans.length === 0) {
            return Card('✨', __('Upgrade Plan', 'multilingual-press-zone'), '', [
                EmptyState(
                    '🎉',
                    __('You are on the highest tier plan', 'multilingual-press-zone'),
                    __('Thank you for your support!', 'multilingual-press-zone')
                )
            ], { staggerIndex: 2 });
        }

        return Card('⬆️', __('Upgrade Your Plan', 'multilingual-press-zone'), __('Unlock more features and add more translation languages.', 'multilingual-press-zone'), [
            el('div', { class: 'mpz-plans-grid' }, ...upgradePlans)
        ], { staggerIndex: 2 });
    }

    /**
     * Render activation card (for inactive licenses)
     *
     * @return {HTMLElement} Activation card element
     */
    renderActivationCard() {
        let licenseKeyInput;

        const cardBody = el('div', { class: 'mpz-activation-form' },
            el('p', { class: 'mpz-activation-intro' },
                __('Enter your license key to activate Multilingual Press Zone and start translating your content.', 'multilingual-press-zone')
            ),

            el('div', { class: 'mpz-form-group' },
                el('label', { for: 'license-key' }, __('License Key', 'multilingual-press-zone')),
                licenseKeyInput = el('input', {
                    type: 'text',
                    id: 'license-key',
                    class: 'mpz-input mpz-license-key-input',
                    placeholder: 'MULT-XXXX-XXXX-XXXX',
                    pattern: 'MULT-[A-Z0-9]{4}-[A-Z0-9]{4}-[A-Z0-9]{4}',
                    maxlength: 19
                }),
                el('p', { class: 'mpz-form-help' },
                    __('Enter your license key in the format MULT-XXXX-XXXX-XXXX', 'multilingual-press-zone')
                )
            ),

            el('div', { class: 'mpz-activation-actions' },
                Button({
                    label: __('Activate License', 'multilingual-press-zone'),
                    variant: 'primary',
                    onClick: () => this.handleActivate(licenseKeyInput.value)
                }),
                el('a', {
                    href: 'https://api.press.zone/pricing',
                    target: '_blank',
                    class: 'mpz-link'
                }, __('Don\'t have a license? Purchase one', 'multilingual-press-zone'))
            )
        );

        // Auto-format license key input
        licenseKeyInput.addEventListener('input', (e) => {
            const value = e.target.value.toUpperCase();
            // Extract content after prefix: strip dashes from the part after MULT-
            let raw;
            const prefixMatch = value.match(/^MULT[-\s]*(.*)/i);
            if (prefixMatch) {
                raw = prefixMatch[1].replace(/[^A-Z0-9]/g, '');
            } else {
                raw = value.replace(/[^A-Z0-9]/g, '');
            }
            // Limit to 12 alphanumeric chars after prefix
            raw = raw.substring(0, 12);
            // Format into MULT-XXXX-XXXX-XXXX
            const parts = ['MULT'];
            for (let i = 0; i < raw.length; i += 4) {
                parts.push(raw.substring(i, i + 4));
            }
            e.target.value = parts.join('-');
        });

        return Card('🔓', __('Activate Your License', 'multilingual-press-zone'), '', [cardBody], { staggerIndex: 0 });
    }

    /**
     * Render plans comparison card
     *
     * @return {HTMLElement} Plans card element
     */
    renderPlansCard() {
        const planCards = Object.entries(LICENSE_PLANS).map(([key, plan]) =>
            this.renderPlanOption(key, plan, true)
        );

        return Card('🧾', __('Choose Your Plan', 'multilingual-press-zone'), __('Select the plan that best fits your translation needs.', 'multilingual-press-zone'), [
            el('div', { class: 'mpz-plans-grid mpz-plans-grid--comparison' }, ...planCards)
        ], { staggerIndex: 1 });
    }

    /**
     * Render individual plan option
     *
     * @param {string} key - Plan key
     * @param {Object} plan - Plan data
     * @param {boolean} showFeatures - Whether to show full features list
     * @return {HTMLElement} Plan option element
     */
    renderPlanOption(key, plan, showFeatures = false) {
        const features = showFeatures ? plan.features : plan.features.slice(0, 3);
        const isUpgrade = this.license && this.license.status === 'active' && this.isPlanUpgrade(this.license.plan, key);

        return el('div', {
            class: 'mpz-plan-option' + (plan.popular ? ' mpz-plan-option--popular' : '')
        },
            plan.popular ? el('div', { class: 'mpz-plan-badge' }, __('Popular', 'multilingual-press-zone')) : null,

            el('h3', { class: 'mpz-plan-name' }, plan.name),

            el('div', { class: 'mpz-plan-price' },
                el('span', { class: 'mpz-plan-price__amount' }, '$' + plan.price),
                el('span', { class: 'mpz-plan-price__interval' }, '/ ' + __('year', 'multilingual-press-zone'))
            ),

            el('ul', { class: 'mpz-plan-features' },
                ...features.map(feature =>
                    el('li', {},
                        el('span', { class: 'dashicons dashicons-yes', 'aria-hidden': 'true' }),
                        feature
                    )
                )
            ),

            Button({
                label: isUpgrade ? __('Upgrade', 'multilingual-press-zone') : __('Select Plan', 'multilingual-press-zone'),
                variant: plan.popular ? 'primary' : 'secondary',
                onClick: isUpgrade
                    ? () => this.showUpgradeModal(plan.slug || key, plan)
                    : () => window.open(`https://api.press.zone/pricing?plan=${key}`, '_blank')
            })
        );
    }

    /**
     * Show upgrade confirmation modal
     *
     * @param {string} tierSlug - Backend tier slug
     * @param {Object} plan - Plan data
     */
    showUpgradeModal(tierSlug, plan) {
        const currentPlan = LICENSE_PLANS[this.license.plan] || LICENSE_PLANS.personal;

        const modal = new Modal({
            title: __('Upgrade to', 'multilingual-press-zone') + ' ' + plan.name,
            width: '500px',
        });

        let cardNumberInput;
        let confirmBtn;
        let paypalBtn;

        const content = el('div', { class: 'mpz-upgrade-modal' },
            // Plan comparison
            el('div', { class: 'mpz-upgrade-comparison' },
                el('div', { class: 'mpz-upgrade-comparison__from' },
                    el('span', { class: 'mpz-upgrade-comparison__label' }, __('Current Plan', 'multilingual-press-zone')),
                    el('span', { class: 'mpz-upgrade-comparison__plan' }, currentPlan.name),
                    el('span', { class: 'mpz-upgrade-comparison__price' }, '$' + currentPlan.price + '/' + __('year', 'multilingual-press-zone'))
                ),
                el('div', { class: 'mpz-upgrade-comparison__arrow' }, '\u2192'),
                el('div', { class: 'mpz-upgrade-comparison__to' },
                    el('span', { class: 'mpz-upgrade-comparison__label' }, __('New Plan', 'multilingual-press-zone')),
                    el('span', { class: 'mpz-upgrade-comparison__plan' }, plan.name),
                    el('span', { class: 'mpz-upgrade-comparison__price' }, '$' + plan.price + '/' + __('year', 'multilingual-press-zone'))
                )
            ),

            // Payment method (mock)
            el('div', { class: 'mpz-upgrade-payment' },
                el('h4', { class: 'mpz-upgrade-payment__title' }, __('Payment Method', 'multilingual-press-zone')),
                el('div', { class: 'mpz-form-group' },
                    el('label', { for: 'upgrade-card' }, __('Card Number', 'multilingual-press-zone')),
                    cardNumberInput = el('input', {
                        type: 'text',
                        id: 'upgrade-card',
                        class: 'mpz-input',
                        placeholder: '4242 4242 4242 4242',
                        maxlength: 19,
                        autocomplete: 'cc-number',
                    })
                ),
                el('div', { class: 'mpz-upgrade-payment__row' },
                    el('div', { class: 'mpz-form-group' },
                        el('label', { for: 'upgrade-expiry' }, __('Expiry', 'multilingual-press-zone')),
                        el('input', {
                            type: 'text',
                            id: 'upgrade-expiry',
                            class: 'mpz-input',
                            placeholder: 'MM/YY',
                            maxlength: 5,
                            autocomplete: 'cc-exp',
                        })
                    ),
                    el('div', { class: 'mpz-form-group' },
                        el('label', { for: 'upgrade-cvc' }, __('CVC', 'multilingual-press-zone')),
                        el('input', {
                            type: 'text',
                            id: 'upgrade-cvc',
                            class: 'mpz-input',
                            placeholder: '123',
                            maxlength: 4,
                            autocomplete: 'cc-csc',
                        })
                    )
                ),
                el('p', { class: 'mpz-form-help' },
                    __('You will be charged $', 'multilingual-press-zone') + plan.price + '/' + __('year', 'multilingual-press-zone') + '.'
                )
            )
        );

        // Auto-format card number with spaces
        cardNumberInput.addEventListener('input', (e) => {
            const raw = e.target.value.replace(/\D/g, '').substring(0, 16);
            e.target.value = raw.replace(/(\d{4})(?=\d)/g, '$1 ');
        });

        const setButtonsBusy = (busy) => {
            if (confirmBtn) confirmBtn.disabled = busy;
            if (paypalBtn) paypalBtn.disabled = busy;
        };

        const actions = [
            Button({
                label: __('Cancel', 'multilingual-press-zone'),
                variant: 'ghost',
                onClick: () => modal.close()
            }),
            confirmBtn = Button({
                label: __('Pay & Upgrade', 'multilingual-press-zone'),
                variant: 'primary',
                onClick: async () => {
                    const cardNumber = cardNumberInput.value.replace(/\s/g, '');
                    if (cardNumber.length < 13) {
                        Toast.error(__('Please enter a valid card number.', 'multilingual-press-zone'));
                        return;
                    }

                    setButtonsBusy(true);
                    confirmBtn.textContent = __('Processing...', 'multilingual-press-zone');

                    try {
                        const response = await api.post('/onboarding/upgrade', {
                            tier_slug: tierSlug,
                            billing_cycle: 'annual',
                        });

                        if (response.success) {
                            modal.close();
                            Toast.success(__('Plan upgraded successfully!', 'multilingual-press-zone'));
                            await this.render();
                        } else {
                            Toast.error(response.message || __('Upgrade failed.', 'multilingual-press-zone'));
                            setButtonsBusy(false);
                            confirmBtn.textContent = __('Pay & Upgrade', 'multilingual-press-zone');
                        }
                    } catch (error) {
                        logger.error('Upgrade failed', error);
                        Toast.error(error.message || __('Upgrade failed. Please try again.', 'multilingual-press-zone'));
                        setButtonsBusy(false);
                        confirmBtn.textContent = __('Pay & Upgrade', 'multilingual-press-zone');
                    }
                }
            }),
            paypalBtn = Button({
                label: __('Continue with PayPal', 'multilingual-press-zone'),
                variant: 'secondary',
                onClick: async () => {
                    // Open a blank tab synchronously inside the click handler so popup
                    // blockers treat it as a user-initiated gesture.
                    const popup = window.open('about:blank', '_blank');

                    setButtonsBusy(true);
                    paypalBtn.textContent = __('Opening PayPal...', 'multilingual-press-zone');

                    try {
                        const response = await api.post('/onboarding/paypal-checkout', {
                            tier_slug: tierSlug,
                            billing_cycle: 'annual',
                        });

                        if (!response.approval_url || !response.checkout_session_id) {
                            if (popup) popup.close();
                            Toast.error(__('Invalid response from payment service. Please try again.', 'multilingual-press-zone'));
                            setButtonsBusy(false);
                            paypalBtn.textContent = __('Continue with PayPal', 'multilingual-press-zone');
                            return;
                        }

                        // Navigate the pre-opened tab to the PayPal approval URL.
                        if (popup) {
                            popup.location.href = response.approval_url;
                        } else {
                            // Popup was blocked — fall back to same-tab redirect.
                            window.location.href = response.approval_url;
                            return;
                        }

                        // Switch modal content to waiting state and start polling.
                        this._startUpgradePaypalPolling(response.checkout_session_id, modal, tierSlug);
                    } catch (error) {
                        if (popup) popup.close();
                        logger.error('PayPal checkout initiation failed', error);
                        // Surface a helpful message if the user needs to log in first.
                        const errMsg = (error.code === 'requires_login' || error.status === 401)
                            ? __('To use PayPal checkout for upgrades, please log into your Press.Zone account first via the onboarding flow.', 'multilingual-press-zone')
                            : (error.message || __('Failed to start PayPal checkout. Please try again.', 'multilingual-press-zone'));
                        Toast.error(errMsg);
                        setButtonsBusy(false);
                        paypalBtn.textContent = __('Continue with PayPal', 'multilingual-press-zone');
                    }
                }
            })
        ];

        modal.render(content, actions);
        modal.open();
    }

    /**
     * Show PayPal waiting state inside the upgrade modal and start polling.
     *
     * @param {string} sessionId - Checkout session UUID
     * @param {Modal} modal - The open modal instance
     * @param {string} tierSlug - Tier slug for error-recovery context
     */
    _startUpgradePaypalPolling(sessionId, modal, tierSlug) {
        // Replace modal body with waiting indicator.
        const waitingContent = el('div', { class: 'mpz-paypal-waiting' },
            el('span', { class: 'spinner is-active' }),
            el('p', {}, __('Waiting for PayPal confirmation...', 'multilingual-press-zone')),
            el('p', { class: 'mpz-form-help' }, __('Complete the payment in the PayPal tab. This will update automatically.', 'multilingual-press-zone'))
        );

        const cancelBtn = Button({
            label: __('Cancel', 'multilingual-press-zone'),
            variant: 'ghost',
            onClick: () => {
                this._stopUpgradePaypalPolling();
                modal.close();
            }
        });

        modal.updateContent(waitingContent);
        modal.updateActions([cancelBtn]);

        const pollDeadline = Date.now() + 5 * 60 * 1000;

        this._upgradePaypalPollInterval = setInterval(async () => {
            if (Date.now() > pollDeadline) {
                this._stopUpgradePaypalPolling();
                modal.close();
                Toast.error(__('PayPal confirmation timed out. Please check your license page or try again.', 'multilingual-press-zone'));
                return;
            }

            try {
                const response = await api.get('/onboarding/paypal-session/' + sessionId);
                const status = response.status;

                if (status === 'retrieved') {
                    this._stopUpgradePaypalPolling();
                    modal.close();
                    Toast.success(__('Plan upgraded successfully via PayPal!', 'multilingual-press-zone'));
                    await this.render();
                } else if (status === 'already_retrieved') {
                    this._stopUpgradePaypalPolling();
                    modal.close();
                    Toast.success(__('Payment confirmed. Your plan has been upgraded.', 'multilingual-press-zone'));
                    await this.render();
                }
                // pending / 503 NOT_READY: keep polling.
            } catch (err) {
                this._stopUpgradePaypalPolling();
                modal.close();
                const code = err.code || err.status || '';
                if (code === 410) {
                    Toast.error(__('PayPal session expired or payment failed. Please try again.', 'multilingual-press-zone'));
                } else if (code === 404) {
                    Toast.error(__('PayPal session not found. Please try again.', 'multilingual-press-zone'));
                } else {
                    Toast.error(err.message || __('Failed to confirm PayPal payment. Please check your license page.', 'multilingual-press-zone'));
                }
            }
        }, 3000);
    }

    _stopUpgradePaypalPolling() {
        if (this._upgradePaypalPollInterval) {
            clearInterval(this._upgradePaypalPollInterval);
            this._upgradePaypalPollInterval = null;
        }
    }

    /**
     * Load license data from API
     */
    async loadLicense() {
        try {
            const response = await api.get('/license');
            this.license = response.license;

            if (this.license && this.license.status === 'active') {
                await this.loadUsage();
            }

            logger.info('License data loaded', this.license);
        } catch (error) {
            logger.error('Failed to load license', error);
            this.license = null;
            Toast.error(error?.message || __('Failed to load license. Please refresh and try again.', 'multilingual-press-zone'));
        }
    }

    /**
     * Load usage statistics from API
     */
    async loadUsage() {
        try {
            const response = await api.get('/license/usage');
            this.usage = response.usage;
            logger.info('Usage data loaded', this.usage);
        } catch (error) {
            logger.error('Failed to load usage', error);
            this.usage = {
                languages_used: 0,
                languages_allowed: 0,
            };
            Toast.error(error?.message || __('Failed to load usage statistics.', 'multilingual-press-zone'));
        }
    }

    /**
     * Handle license activation
     *
     * @param {string} licenseKey - License key to activate
     */
    async handleActivate(licenseKey) {
        // Validate format
        if (!this.validateLicenseKey(licenseKey)) {
            this.showError(__('Invalid license key format. Please use MULT-XXXX-XXXX-XXXX', 'multilingual-press-zone'));
            return;
        }

        try {
            const response = await api.post('/license/activate', { license_key: licenseKey });

            if (response.success) {
                this.showSuccess(__('License activated successfully!', 'multilingual-press-zone'));
                this.license = response.license;
                await this.render();
            } else {
                this.showError(response.message || __('Failed to activate license.', 'multilingual-press-zone'));
            }
        } catch (error) {
            logger.error('License activation failed', error);
            this.showError(error.message || __('Failed to activate license.', 'multilingual-press-zone'));
        }
    }

    /**
     * Handle license deactivation
     */
    async handleDeactivate() {
        if (!confirm(__('Are you sure you want to deactivate your license on this site?', 'multilingual-press-zone'))) {
            return;
        }

        try {
            const response = await api.post('/license/deactivate');

            if (response.success) {
                this.showSuccess(__('License deactivated successfully.', 'multilingual-press-zone'));
                this.license = null;
                await this.render();
            } else {
                this.showError(response.message || __('Failed to deactivate license.', 'multilingual-press-zone'));
            }
        } catch (error) {
            logger.error('License deactivation failed', error);
            this.showError(error.message || __('Failed to deactivate license.', 'multilingual-press-zone'));
        }
    }

    /**
     * Validate license key format
     *
     * @param {string} key - License key to validate
     * @return {boolean} True if valid
     */
    validateLicenseKey(key) {
        return /^MULT-[A-Z0-9]{4}-[A-Z0-9]{4}-[A-Z0-9]{4}$/.test(key);
    }

    /**
     * Mask license key for display
     *
     * @param {string} key - Full license key
     * @return {string} Masked key
     */
    maskLicenseKey(key) {
        if (!key) return '';
        const parts = key.split('-');
        if (parts.length !== 4) return key;
        return `${parts[0]}-XXXX-XXXX-${parts[3]}`;
    }

    /**
     * Get license status text
     *
     * @param {string} status - License status
     * @return {string} Status text
     */
    getLicenseStatusText(status) {
        const statusMap = {
            active: __('Active', 'multilingual-press-zone'),
            expired: __('Expired', 'multilingual-press-zone'),
            invalid: __('Invalid', 'multilingual-press-zone'),
            suspended: __('Suspended', 'multilingual-press-zone')
        };
        return statusMap[status] || __('Unknown', 'multilingual-press-zone');
    }

    /**
     * Get license status badge variant
     *
     * @param {string} status - License status
     * @return {string} Badge variant
     */
    getLicenseStatusVariant(status) {
        const variantMap = {
            active: 'success',
            expired: 'warning',
            invalid: 'error',
            suspended: 'error'
        };
        return variantMap[status] || 'default';
    }

    /**
     * Check if plan is an upgrade from current
     *
     * @param {string} currentPlan - Current plan key
     * @param {string} targetPlan - Target plan key
     * @return {boolean} True if upgrade
     */
    isPlanUpgrade(currentPlan, targetPlan) {
        const planOrder = ['personal', 'professional', 'agency'];
        const currentIndex = planOrder.indexOf(currentPlan);
        const targetIndex = planOrder.indexOf(targetPlan);
        return targetIndex > currentIndex;
    }

    /**
     * Format date for display
     *
     * @param {string} dateString - ISO date string
     * @return {string} Formatted date
     */
    formatDate(dateString) {
        if (!dateString) return '-';
        const date = new Date(dateString);
        return date.toLocaleDateString(undefined, { year: 'numeric', month: 'long', day: 'numeric' });
    }

    /**
     * Show success message
     *
     * @param {string} message - Message to show
     */
    showSuccess(message) {
        Toast.success(message);
    }

    /**
     * Show error message
     *
     * @param {string} message - Message to show
     */
    showError(message) {
        Toast.error(message);
    }

    /**
     * Destroy the page
     */
    destroy() {
        this._stopUpgradePaypalPolling();

        if (this.progressBar) {
            this.progressBar.destroy();
            this.progressBar = null;
        }

        clear(this.container);

        this.license = null;
        this.usage = null;
        this.usageChart = null;

        logger.info('Licensing page destroyed');
    }
}
